import { createReducer } from '@reduxjs/toolkit';
import {
  addElementIdToSelection,
  cameraGoto,
  clearSelectedPoint,
  clearSelectedScan,
  clearSelectedTrenchId,
  clearSelection,
  removeElementIdFromSelection,
  setCamera,
  setCameraViewport,
  setMapStyle,
  setScanSelection,
  setSelectedElementIds,
  setSelectedPointId,
  setSelectedProjectId,
  setSelectedScanId,
  setSelectedTrenchId,
  setVisibleElementUsage,
  setVisibleScans,
} from './actions';
import type { Viewer3dState } from './interface';

const updateState =
  <K extends keyof Viewer3dState>(prop: K) =>
  (state: Viewer3dState, update: { payload: Viewer3dState[K] }) => {
    state[prop] = update.payload;
  };

const initialState: Viewer3dState = {
  selectedTrenchId: undefined,
  camera: {},
  initialCameraSet: false,
  visibleScans: 'ONLY_VISIBLE',
  visibleElementUsage: {
    SPEEDPIPE_BUNDLE: true,
    SPEEDPIPE: true,
    OTHER: true,
    PROTECTIVE_PIPE: true,
  },
  mapStyle: 'NONE',
  selectedElementIds: [],
};

export const viewer3dReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setMapStyle, updateState('mapStyle'))
    .addCase(setVisibleScans, updateState('visibleScans'))
    .addCase(setCameraViewport, updateState('cameraViewport'))
    .addCase(setSelectedProjectId, (state, action) => {
      state.selectedProjectId = action.payload;
      state.initialCameraSet = false;
    })
    .addCase(setSelectedScanId, (state, action) => {
      const payload = action.payload;
      if (typeof payload === 'string') {
        state.selectedScanId = payload;
      } else if (payload) {
        state.selectedScanId = payload.id;
      } else {
        state.selectedScanId = undefined;
      }
    })
    .addCase(setScanSelection, updateState('scanSelection'))
    .addCase(setSelectedPointId, updateState('selectedPointId'))
    .addCase(setSelectedTrenchId, updateState('selectedTrenchId'))
    .addCase(setVisibleElementUsage, (state, action) => ({
      ...state,
      visibleElementUsage: {
        ...state.visibleElementUsage,
        ...action.payload,
      },
    }))
    .addCase(clearSelectedScan, (state) => ({
      ...state,
      selectedScanId: undefined,
    }))
    .addCase(clearSelectedPoint, (state) => ({ ...state, selectedPointId: undefined }))
    .addCase(clearSelectedTrenchId, (state) => ({
      ...state,
      selectedTrenchId: undefined,
      selectedPointId: undefined, // when no trench is selected there can no point be selected either!
    }))
    .addCase(clearSelection, (state, action) => ({
      ...state,
      selectedScanId: undefined,
      selectedTrenchId: undefined,
      selectedElementIds: [],
      selectedPointId: undefined,
      initialCameraSet: action.payload?.keepCamera === false ? false : state.initialCameraSet,
    }))
    .addCase(setSelectedElementIds, (state, action) => ({
      ...state,
      selectedElementIds: action.payload,
    }))
    .addCase(addElementIdToSelection, (state, action) => ({
      ...state,
      selectedElementIds: [...state.selectedElementIds, action.payload],
    }))
    .addCase(removeElementIdFromSelection, (state, action) => ({
      ...state,
      selectedElementIds: state.selectedElementIds.filter((id) => id !== action.payload),
    }))
    .addCase(setCamera, (state, action) => ({
      ...state,
      camera: {
        ...state.camera,
        ...action.payload,
      },
    }))
    .addCase(cameraGoto, (state, action) => {
      if (action.payload?.initial && state.initialCameraSet) return state;
      return {
        ...state,
        camera: {
          ...state.camera,
          goTo: action.payload,
        },
        initialCameraSet: action.payload?.initial ? true : state.initialCameraSet,
      };
    });
});
